import { useEffect, useMemo, useRef, useState, type MouseEvent } from 'react';
import { useParams } from 'react-router-dom';
import { MessageCircle, Clock, MapPin, UtensilsCrossed, X, Share2, Copy, Check, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { trackPageView, trackEvent, trackWhatsAppRedirect, flushEvents } from '../lib/analytics';
import { isMobile, buildWhatsAppUrl } from '../lib/whatsappDeeplink';
import { appendReferralCode } from '../lib/utils';
import { useCMS } from '../hooks/useCMS';
import OfferDescription from '../components/OfferDescription';

interface Offer {
  id: number;
  key: string;
  region: 'local' | 'mainland';
  restaurantName: string;
  title: string;
  description?: string;
  redeemInstructions?: string;
  imageUrl?: string;
  validUntil?: number | null;
  maxClaims?: number | null;
  claimsCount?: number;
  sortOrder?: number;
  createdAt?: number;
}

const FALLBACK_PHONE = '85260771493';
const IMPRESSION_TRACKED_KEY = 'x1493_offer_impressions_tracked';

type RegionFilter = 'all' | 'local' | 'mainland';

function useOfferImpression(offer: Offer | null) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!offer || typeof window === 'undefined' || !('IntersectionObserver' in window)) return;

    const element = ref.current;
    if (!element) return;

    // Track each offer once per session
    const trackedKeys = new Set<string>(
      JSON.parse(window.sessionStorage.getItem(IMPRESSION_TRACKED_KEY) || '[]') as string[]
    );
    if (trackedKeys.has(offer.key)) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            trackEvent('offer_impression', {
              offerKey: offer.key,
              offerId: offer.id,
              region: offer.region,
              restaurant: offer.restaurantName,
            });
            flushEvents();
            trackedKeys.add(offer.key);
            window.sessionStorage.setItem(IMPRESSION_TRACKED_KEY, JSON.stringify(Array.from(trackedKeys)));
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.5 }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [offer]);

  return ref;
}

const REGION_TABS: { value: RegionFilter; label: string }[] = [
  { value: 'all', label: '全部' },
  { value: 'local', label: '香港本地' },
  { value: 'mainland', label: '內地' },
];

const PAGE_SIZE = 15;

// Social-proof badge (👥 已有 X 人領取) is counterproductive while claim counts are small.
// Flip to true once numbers look impressive.
const SHOW_CLAIMS_SOCIAL_PROOF = false;

function pageList(current: number, total: number): (number | '…')[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = new Set<number>([1, total, current - 1, current, current + 1]);
  const sorted = [...pages].filter((p) => p >= 1 && p <= total).sort((a, b) => a - b);
  const out: (number | '…')[] = [];
  sorted.forEach((p, i) => {
    if (i > 0 && p - (sorted[i - 1] as number) > 1) out.push('…');
    out.push(p);
  });
  return out;
}

function OfferModal({
  offer,
  phone,
  mobile,
  onClose,
}: {
  offer: Offer;
  phone: string;
  mobile: boolean;
  onClose: () => void;
}) {
  const claimUrl = (o: Offer) => buildWhatsAppUrl(phone, `香港辣谷優惠 OFFER:${o.key}`, !mobile);
  // Short share link (/c/{key}) — speakable, no query string, safe in chat previews
  // 轉介歸因鏈：帶活躍轉介碼的訪客分享時自動接上 ?r=（無碼則保持乾淨短連結）
  const shareUrl = appendReferralCode(`${window.location.origin}/c/${offer.key}`);
  const [copied, setCopied] = useState(false);

  const copyShareUrl = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
    } catch {
      const el = document.createElement('textarea');
      el.value = shareUrl;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleClaimClick = (e: MouseEvent) => {
    trackEvent('offer_claim_click', { offerKey: offer.key, region: offer.region, restaurant: offer.restaurantName });
    trackWhatsAppRedirect('offer_claim', offer.key);
    flushEvents();
    if (mobile) {
      e.preventDefault();
      window.location.href = claimUrl(offer);
    }
  };

  const formatDate = (ts: number) => new Date(ts * 1000).toLocaleDateString('zh-HK');

  const nowSec = Date.now() / 1000;
  const expired = !!offer.validUntil && offer.validUntil > 0 && offer.validUntil < nowSec;
  const remaining =
    offer.maxClaims && offer.maxClaims > 0
      ? Math.max(0, offer.maxClaims - (offer.claimsCount || 0))
      : null;
  const full = remaining === 0;
  const disabled = expired || full;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="offer-modal-title"
    >
      <div
        className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto bg-white rounded-2xl shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 z-10 p-2 rounded-full bg-black/40 text-white hover:bg-black/60 transition"
          aria-label="關閉"
        >
          <X size={20} />
        </button>

        <div className="aspect-[16/10] overflow-hidden bg-stone-100 relative">
          {offer.imageUrl ? (
            <img
              src={offer.imageUrl}
              alt={offer.restaurantName}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <UtensilsCrossed className="text-stone-300" size={72} />
            </div>
          )}
          <span
            className={`absolute top-3 left-3 text-xs font-bold px-2.5 py-1 rounded-full ${
              offer.region === 'mainland' ? 'bg-blue-600 text-white' : 'bg-x1493-primary text-white'
            }`}
          >
            <MapPin size={11} className="inline mr-1 -mt-0.5" />
            {offer.region === 'mainland' ? '內地' : '香港'}
          </span>
        </div>

        <div className="p-6">
          <h3 className="text-sm font-bold text-stone-500 uppercase tracking-wide">{offer.restaurantName}</h3>
          <h2 id="offer-modal-title" className="text-2xl font-bold text-stone-900 mt-1">
            {offer.title}
          </h2>

          <OfferDescription
            text={offer.description}
            className="mt-4 text-stone-700 leading-relaxed [&_p]:mb-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:mb-1 [&_strong]:font-bold [&_a]:text-red-600 [&_a]:underline"
          />

          {offer.redeemInstructions ? (
            <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-4">
              <p className="text-sm font-bold text-amber-800 mb-1">🎁 兌換說明</p>
              <p className="text-sm text-amber-700 whitespace-pre-line">{offer.redeemInstructions}</p>
            </div>
          ) : null}

          <div className="mt-5 space-y-2 text-sm text-stone-600">
            {offer.validUntil ? (
              <p className="flex items-center gap-2">
                <Clock size={16} className="text-x1493-primary" />
                有效期至 {formatDate(offer.validUntil)}
              </p>
            ) : null}
            {remaining !== null && !expired && (
              <p className={`flex items-center gap-2 ${remaining <= 10 ? 'text-x1493-primary font-bold' : ''}`}>
                <UtensilsCrossed size={16} className={remaining <= 10 ? 'text-x1493-primary' : 'text-stone-400'} />
                {remaining > 0 ? `⚡ 僅剩 ${remaining} 個名額` : '名額已滿'}
              </p>
            )}
          </div>

          <div className="mt-5 rounded-xl border border-stone-200 bg-stone-50 p-3">
            <p className="text-xs font-bold text-stone-500 mb-2 flex items-center gap-1">
              <Share2 size={13} /> 優惠專屬連結
            </p>
            <div className="flex items-center gap-2">
              <code className="flex-1 min-w-0 truncate text-xs text-stone-600 bg-white border border-stone-200 rounded-lg px-2.5 py-2">
                {shareUrl}
              </code>
              <button
                type="button"
                onClick={copyShareUrl}
                className={`inline-flex items-center gap-1 shrink-0 px-3 py-2 rounded-lg text-xs font-bold transition ${
                  copied
                    ? 'bg-green-600 text-white'
                    : 'bg-x1493-primary text-white hover:bg-red-700'
                }`}
              >
                {copied ? <Check size={14} /> : <Copy size={14} />}
                {copied ? '已複製' : '複製'}
              </button>
            </div>
          </div>

          <div className="mt-6">
            {disabled ? (
              <div className="w-full text-center py-3 rounded-2xl bg-stone-200 text-stone-400 font-bold">
                {expired ? '優惠已截止' : '名額已滿'}
              </div>
            ) : (
              <a
                href={claimUrl(offer)}
                target={mobile ? undefined : '_blank'}
                rel={mobile ? undefined : 'noopener noreferrer'}
                onClick={handleClaimClick}
                className="w-full inline-flex items-center justify-center gap-2 bg-[#25D366] text-white font-bold py-3 rounded-2xl hover:bg-[#128C7E] transition active:scale-95"
              >
                <MessageCircle size={20} />
                在 WhatsApp 領取優惠
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function OfferCard({
  offer,
  onSelect,
  claimUrl,
  onClaimClick,
  formatDate,
  isExpired,
  isFull,
  remainingOf,
  mobile,
  topRank,
  isNew,
}: {
  offer: Offer;
  onSelect: (offer: Offer) => void;
  claimUrl: (offer: Offer) => string;
  onClaimClick: (e: MouseEvent, offer: Offer) => void;
  formatDate: (ts: number) => string;
  isExpired: (o: Offer) => boolean;
  isFull: (o: Offer) => boolean;
  remainingOf: (o: Offer) => number | null;
  mobile: boolean;
  topRank?: number;
  isNew?: boolean;
}) {
  const impressionRef = useOfferImpression(offer);
  const expired = isExpired(offer);
  const full = isFull(offer);
  const remaining = remainingOf(offer);
  const disabled = expired || full;

  return (
    <div
      ref={impressionRef}
      onClick={() => onSelect(offer)}
      className="bg-white rounded-xl sm:rounded-2xl overflow-hidden shadow-sm border border-stone-200 flex flex-col cursor-pointer hover:shadow-md transition"
    >
      <div className="aspect-[4/3] overflow-hidden bg-stone-100 relative">
        {offer.imageUrl ? (
          <img
            src={offer.imageUrl}
            alt={offer.restaurantName}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <UtensilsCrossed className="text-stone-300" size={mobile ? 32 : 56} />
          </div>
        )}
        <span
          className={`absolute top-2 left-2 sm:top-3 sm:left-3 text-[10px] sm:text-xs font-bold px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-full ${
            offer.region === 'mainland'
              ? 'bg-blue-600 text-white'
              : 'bg-x1493-primary text-white'
          }`}
        >
          <MapPin size={mobile ? 10 : 11} className="inline mr-0.5 sm:mr-1 -mt-0.5" />
          {offer.region === 'mainland' ? '內地' : '香港'}
        </span>
        {topRank && topRank <= 3 && (
          <span className="absolute top-2 right-2 sm:top-3 sm:right-3 text-[10px] sm:text-xs font-bold px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-full bg-amber-500 text-white shadow">
            🔥 No.{topRank}
          </span>
        )}
        {isNew && !topRank && (
          <span className="absolute top-2 right-2 sm:top-3 sm:right-3 text-[10px] sm:text-xs font-bold px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-full bg-green-500 text-white shadow">
            NEW
          </span>
        )}
        {SHOW_CLAIMS_SOCIAL_PROOF && (offer.claimsCount || 0) > 0 && (
          <span className="absolute bottom-2 left-2 text-[10px] sm:text-xs font-bold px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-full bg-black/55 text-white backdrop-blur-sm">
            👥 已有 {offer.claimsCount} 人領取
          </span>
        )}
        {!SHOW_CLAIMS_SOCIAL_PROOF && remaining !== null && !expired && remaining > 0 && (
          <span
            className={`absolute bottom-2 left-2 text-[10px] sm:text-xs font-bold px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-full text-white backdrop-blur-sm shadow ${
              remaining <= 10 ? 'bg-x1493-primary animate-pulse' : 'bg-black/60'
            }`}
          >
            ⚡ 僅餘 {remaining} 名額
          </span>
        )}
      </div>

      <div className="p-3 sm:p-5 flex flex-col flex-1">
        <h3 className="font-bold text-sm sm:text-lg text-stone-900 leading-tight">{offer.restaurantName}</h3>
        <p className="text-x1493-primary font-bold text-xs sm:text-base mt-0.5 sm:mt-1">{offer.title}</p>

        <div className="mt-2 sm:mt-3 space-y-0.5 sm:space-y-1 text-[10px] sm:text-xs text-stone-500">
          {offer.validUntil ? (
            <p className="flex items-center gap-1">
              <Clock size={mobile ? 10 : 13} /> 有效期至 {formatDate(offer.validUntil)}
            </p>
          ) : null}
          {remaining !== null && !expired && remaining > 0 && (
            <div>
              <p className={remaining <= 10 ? 'text-x1493-primary font-bold' : ''}>
                僅剩 {remaining} / {offer.maxClaims} 個名額
              </p>
              <div className="h-1 sm:h-1.5 mt-1 bg-stone-200 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full ${remaining <= 10 ? 'bg-x1493-primary' : 'bg-stone-400'}`}
                  style={{ width: `${Math.min(100, Math.round((Number(offer.maxClaims) - remaining) / Number(offer.maxClaims) * 100))}%` }}
                />
              </div>
            </div>
          )}
        </div>

        <div className="mt-auto pt-2 sm:pt-4">
          {disabled ? (
            <div className="w-full text-center py-1.5 sm:py-3 rounded-xl sm:rounded-2xl bg-stone-200 text-stone-400 font-bold text-[10px] sm:text-sm">
              {expired ? '已截止' : '已滿'}
            </div>
          ) : (
            <a
              href={claimUrl(offer)}
              target={mobile ? undefined : '_blank'}
              rel={mobile ? undefined : 'noopener noreferrer'}
              onClick={(e) => onClaimClick(e, offer)}
              className="w-full inline-flex items-center justify-center gap-1 sm:gap-2 bg-[#25D366] text-white font-bold py-1.5 sm:py-3 rounded-xl sm:rounded-2xl hover:bg-[#128C7E] transition active:scale-95 text-[10px] sm:text-sm"
            >
              <MessageCircle size={mobile ? 12 : 20} />
              <span className="sm:hidden">WhatsApp 領取</span>
              <span className="hidden sm:inline">在 WhatsApp 領取優惠</span>
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Offers() {
  const [offers, setOffers] = useState<Offer[]>([]);
  const [loading, setLoading] = useState(true);
  const [region, setRegion] = useState<RegionFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [selectedOffer, setSelectedOffer] = useState<Offer | null>(null);
  const { getValue } = useCMS();
  const phone = getValue('site.whatsapp.number', FALLBACK_PHONE) || FALLBACK_PHONE;
  const mobile = useMemo(() => isMobile(), []);
  const { key: offerKeyParam } = useParams<{ key: string }>();

  useEffect(() => {
    trackPageView('/offers');
    fetch('/api/public/offers')
      .then((r) => r.json())
      .then((d) => {
        setOffers(Array.isArray(d) ? d : d.data || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  // Deep link /offers/:key — auto-open the matching offer detail
  useEffect(() => {
    if (!offerKeyParam || loading || offers.length === 0) return;
    const match = offers.find((o) => o.key === offerKeyParam.toLowerCase());
    if (match) setSelectedOffer((cur) => (cur?.key === match.key ? cur : match));
  }, [offerKeyParam, offers, loading]);

  // Keep the address bar in sync so each offer has a shareable URL
  useEffect(() => {
    const url = new URL(window.location.href);
    if (selectedOffer?.key) {
      url.pathname = `/offers/${selectedOffer.key}`;
      window.history.replaceState(null, '', url.toString());
    } else if (offerKeyParam) {
      url.pathname = '/offers';
      window.history.replaceState(null, '', url.toString());
    }
  }, [selectedOffer, offerKeyParam]);

  useEffect(() => {
    if (!selectedOffer) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSelectedOffer(null);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [selectedOffer]);

  const nowSec = Date.now() / 1000;
  const sevenDaysAgo = nowSec - 7 * 24 * 3600;

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return offers.filter(
      (o) =>
        (region === 'all' || o.region === region) &&
        (!q || o.restaurantName.toLowerCase().includes(q) || o.title.toLowerCase().includes(q))
    );
  }, [offers, region, searchQuery]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageOffers = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  // Any filter change sends the visitor back to the first page
  useEffect(() => {
    setPage(1);
  }, [region, searchQuery]);

  useEffect(() => {
    if (page < 1 || page > totalPages) setPage(Math.min(Math.max(1, page), totalPages));
  }, [page, totalPages]);

  // Global top 3 by claim count (across all regions) for the fire badge
  const globalTop3 = useMemo(() => {
    return [...offers]
      .sort((a, b) => (b.claimsCount || 0) - (a.claimsCount || 0))
      .slice(0, 3)
      .map((o) => o.id);
  }, [offers]);

  const isExpired = (o: Offer) => !!o.validUntil && o.validUntil > 0 && o.validUntil < nowSec;
  const remainingOf = (o: Offer): number | null =>
    o.maxClaims && o.maxClaims > 0 ? Math.max(0, o.maxClaims - (o.claimsCount || 0)) : null;
  const isFull = (o: Offer) => remainingOf(o) === 0;

  const claimUrl = (offer: Offer) =>
    buildWhatsAppUrl(phone, `香港辣谷優惠 OFFER:${offer.key}`, !mobile);

  const handleClaimClick = (e: MouseEvent, offer: Offer) => {
    e.stopPropagation();
    trackEvent('offer_claim_click', { offerKey: offer.key, region: offer.region, restaurant: offer.restaurantName });
    trackWhatsAppRedirect('offer_claim', offer.key);
    flushEvents();
    if (mobile) {
      e.preventDefault();
      window.location.href = claimUrl(offer);
    }
  };

  const formatDate = (ts: number) => new Date(ts * 1000).toLocaleDateString('zh-HK');

  return (
    <div className="min-h-screen bg-stone-50">
      <Header />

      <section className="relative bg-x1493-dark text-white py-12 md:py-16 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: "url('/media/media/1786030012_hkgspicyvalley_bg.jpeg')" }}
        />
        <div className="absolute inset-0 bg-x1493-dark/70" />
        <div className="max-w-6xl mx-auto px-4 relative z-10">
          <h1 className="text-3xl md:text-4xl font-bold mb-3">食肆優惠</h1>
          <p className="text-white/80">辣谷之友專享餐廳禮遇 · 一撳經 WhatsApp 領取</p>
        </div>
      </section>

      <section className="py-8">
        <div className="max-w-6xl mx-auto px-4">
          {/* Region tabs + restaurant search */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-8">
            <div className="flex gap-2 flex-wrap">
              {REGION_TABS.map((t) => (
                <button
                  key={t.value}
                  onClick={() => setRegion(t.value)}
                  className={`px-5 py-2 rounded-full text-sm font-medium transition ${
                    region === t.value
                      ? 'bg-x1493-primary text-white shadow'
                      : 'bg-white text-stone-600 border border-stone-200 hover:border-x1493-primary'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
            <div className="relative sm:ml-auto sm:w-72">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="搜尋餐廳名字"
                aria-label="搜尋餐廳名字"
                className="w-full pl-9 pr-9 py-2 rounded-full text-sm bg-white border border-stone-200 text-stone-800 placeholder:text-stone-400 focus:outline-none focus:border-x1493-primary focus:ring-2 focus:ring-x1493-primary/20 transition"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                  aria-label="清除搜尋"
                >
                  <X size={15} />
                </button>
              )}
            </div>
          </div>

          {loading ? (
            <div className="text-center py-20 text-stone-500">載入中...</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20 bg-white rounded-2xl border border-stone-200">
              <UtensilsCrossed className="mx-auto text-stone-300 mb-4" size={48} />
              <p className="text-stone-500">
                {searchQuery.trim()
                  ? `搵唔到「${searchQuery.trim()}」嘅餐廳，試下其他關鍵字啦！`
                  : '暫時未有優惠，留意香港辣谷最新消息！'}
              </p>
            </div>
          ) : (
            <>
              <p className="text-xs text-stone-400 mb-4">
                共 {filtered.length} 個優惠
                {searchQuery.trim() ? ` · 搜尋「${searchQuery.trim()}」` : ''}
                {totalPages > 1 ? ` · 第 ${safePage} / ${totalPages} 頁` : ''}
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-6">
                {pageOffers.map((offer) => (
                  <OfferCard
                    key={offer.id}
                    offer={offer}
                    onSelect={setSelectedOffer}
                    claimUrl={claimUrl}
                    onClaimClick={handleClaimClick}
                    formatDate={formatDate}
                    isExpired={isExpired}
                    isFull={isFull}
                    remainingOf={remainingOf}
                    mobile={mobile}
                    topRank={globalTop3.indexOf(offer.id) + 1 || undefined}
                    isNew={!!offer.createdAt && offer.createdAt >= sevenDaysAgo}
                  />
                ))}
              </div>

              {totalPages > 1 && (
                <nav className="flex items-center justify-center gap-1.5 mt-10" aria-label="分頁">
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={safePage === 1}
                    className="inline-flex items-center gap-1 px-3.5 py-2 rounded-full text-sm font-medium bg-white border border-stone-200 text-stone-600 hover:border-x1493-primary hover:text-x1493-primary disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-stone-200 disabled:hover:text-stone-600 transition"
                  >
                    <ChevronLeft size={16} /> 上一頁
                  </button>
                  {pageList(safePage, totalPages).map((p, i) =>
                    p === '…' ? (
                      <span key={`ellipsis-${i}`} className="px-1.5 text-stone-400">
                        …
                      </span>
                    ) : (
                      <button
                        key={p}
                        type="button"
                        onClick={() => setPage(p)}
                        aria-current={p === safePage ? 'page' : undefined}
                        className={`w-9 h-9 rounded-full text-sm font-medium transition ${
                          p === safePage
                            ? 'bg-x1493-primary text-white shadow'
                            : 'bg-white border border-stone-200 text-stone-600 hover:border-x1493-primary hover:text-x1493-primary'
                        }`}
                      >
                        {p}
                      </button>
                    )
                  )}
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={safePage === totalPages}
                    className="inline-flex items-center gap-1 px-3.5 py-2 rounded-full text-sm font-medium bg-white border border-stone-200 text-stone-600 hover:border-x1493-primary hover:text-x1493-primary disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-stone-200 disabled:hover:text-stone-600 transition"
                  >
                    下一頁 <ChevronRight size={16} />
                  </button>
                </nav>
              )}
            </>
          )}

          <p className="text-xs text-stone-400 text-center mt-10 max-w-2xl mx-auto">
            撳掣領取優惠即表示你同意香港辣谷透過 WhatsApp 與你聯絡，並同意你的姓名及電話於核銷時提供予該餐廳（詳見
            <a href="/privacy" className="text-stone-500 hover:text-x1493-primary underline underline-offset-2">隱私條款</a>
            ）。優惠受各餐廳條款及細則約束。
          </p>
        </div>
      </section>

      {selectedOffer && (
        <OfferModal
          offer={selectedOffer}
          phone={phone}
          mobile={mobile}
          onClose={() => setSelectedOffer(null)}
        />
      )}

      <Footer />
    </div>
  );
}
